// ── Frontend mirror of backend permission enforcement ──────────────────────
// Mirrors permit() in backend/middleware/auth.js: admin and super-admin
// always pass; everyone else is gated by their effective PermissionSet
// grants (fetched once via GET /api/auth/permissions, cached in localStorage
// under 'permissions' so it survives page reloads without re-login).
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export const fetchAndStorePermissions = async (token) => {
  try {
    const { data } = await axios.get(`${API}/api/auth/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.setItem('permissions', JSON.stringify(data));
  } catch {
    // Keep the last known grants on transient failures (offline, cold backend)
    // rather than failing closed on every module. A real 401 logs the user out
    // via the global axios interceptor anyway.
  }
};

const getStored = () => JSON.parse(localStorage.getItem('permissions') || 'null');

// True if the current user can view the given module. Admin/super-admin
// always pass (they bypass permission checks server-side too).
export const canView = (module) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.isSuperAdmin || user.role === 'admin') return true;
  const stored = getStored();
  if (!stored) return false;
  if (stored.fullAccess) return true;
  return !!stored.permissions?.[module]?.canView;
};

// Maps a route pathname to the permission module that gates it — mirrors
// the permit(module, ...) calls each backend route file already uses.
// Returns null for routes that aren't module-specific (dashboard, messages,
// landing, etc.) — those need no extra check beyond being logged in.
//
// IMPORTANT: /setups/* is a NAVIGATION grouping, not a permission boundary —
// several "Setups" screens actually configure another module's data and are
// enforced by that module's permit() call on the backend (e.g. Locations is
// served by payables.js under 'supply-chain'; posting groups and the grading
// scale are served by academics.js under 'academics'). Those overrides MUST
// come before the generic '/setups' entry below, since moduleForPath returns
// on the first matching prefix.
const MODULE_PATH_PREFIXES = [
  ['/setups/locations',              'supply-chain'], // routes/payables.js
  ['/setups/posting',                'academics'],     // routes/academics.js
  ['/setups/learner-posting-groups', 'academics'],
  ['/setups/vat-posting-groups',     'academics'],
  ['/setups/dimensions',             'finance'],     // routes/finance.js
  ['/setups/grading-scale',          'academics'],
  ['/setups/grading-systems',        'academics'],
  ['/setups/data-import',            null],            // multi-collection; backend enforces per collection
  ['/setups/approvals',              'setups'],         // approval workflow config

  ['/approvals',     null],            // my approvals inbox — any logged-in user, gated by approver match
  ['/setups',        'setups'],
  ['/payroll',       'payroll'],
  ['/hr',            'hr'],
  ['/procurement',   'supply-chain'],
  ['/payables',      'supply-chain'],
  ['/fixed-assets',  'supply-chain'],
  ['/suppliers',     'supply-chain'],
  ['/supply-chain',  'supply-chain'],
  ['/learners',      'academics'],
  ['/academics',     'academics'],
  ['/fleet',         'fleet'],
  ['/library',       'library'],
  ['/statements',    'finance'],
  ['/payments',      'finance'],
  ['/billing',       'finance'],
  ['/invoices',      'finance'],
  ['/receipts',      'finance'],
  ['/feestructures', 'finance'],
  ['/ledger',        'finance'],
  ['/banks',         'finance'],
  ['/gl-accounts',   'finance'],
  ['/finance',       'finance'],
];

export const moduleForPath = (pathname) => {
  const hit = MODULE_PATH_PREFIXES.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/'));
  return hit ? hit[1] : null;
};

// ── Role-centre landing ───────────────────────────────────────────────────────
// Where a user should land after login (and when they hit "home"): their own
// role centre, matched to what they're allowed to see. Modules are checked in
// priority order, so a Finance Manager (finance + supply-chain) lands on Finance,
// while a pure Procurement user lands on Supply Chain. Admins / full-access users
// get the all-centres dashboard (that IS their role centre). Anyone with no
// single obvious centre falls back to the dashboard grid too.
const ROLE_CENTRES = [
  ['finance',      '/finance'],
  ['academics',    '/academics'],
  ['hr',           '/hr'],
  ['payroll',      '/payroll'],
  ['supply-chain', '/supply-chain'],
];

export const landingPath = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.isSuperAdmin)              return '/admin/schools';
  if (user.role === 'parent')        return '/parent';
  if (user.role === 'classTeacher')  return '/class-teacher-centre';
  if (user.role === 'admin')         return '/dashboard';   // runs everything → full grid
  const stored = getStored();
  if (stored?.fullAccess)            return '/dashboard';
  // Ordinary staff land on their self-service Staff Portal by default. Any
  // back-office module role centres they hold remain reachable from the portal
  // (the "Back to Workspace" link) and by direct navigation.
  return '/staff';
};

// The back-office workspace a staff user can return to from the Staff Portal:
// their module role centre if they hold one, the all-centres dashboard for
// admins / full-access users, or null for pure self-service staff (who then see
// no "Back to Workspace" link). Deliberately excludes portal roles.
export const workspacePath = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.isSuperAdmin)             return '/admin/schools';
  if (user.role === 'admin')         return '/dashboard';
  const stored = getStored();
  if (stored?.fullAccess)            return '/dashboard';
  const hit = ROLE_CENTRES.find(([m]) => canView(m));
  return hit ? hit[1] : null;
};
